import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "appSource" }
const _hoisted_2 = { class: "button-row source" }
const _hoisted_3 = ["options", "value"]
const _hoisted_4 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_5 = { class: "button-row" }
const _hoisted_6 = ["value", "label"]
const _hoisted_7 = {
  key: 1,
  class: "spacer source"
}
const _hoisted_8 = { class: "button-row" }
const _hoisted_9 = ["value", "label"]
const _hoisted_10 = {
  key: 2,
  class: "spacer source"
}
const _hoisted_11 = ["value", "label"]
const _hoisted_12 = { class: "spacer source" }
const _hoisted_13 = ["value", "label"]
const _hoisted_14 = {
  key: 0,
  class: "error"
}
const _hoisted_15 = { class: "spacer source" }
const _hoisted_16 = ["value", "label", "disabled"]
const _hoisted_17 = { class: "spacer source" }
const _hoisted_18 = ["value", "label", "options", "disabled"]
const _hoisted_19 = {
  key: 0,
  class: "spacer source builder-image"
}
const _hoisted_20 = ["checked"]
const _hoisted_21 = ["value", "disabled"]

import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import jsyaml from 'js-yaml';

import Application from '../../models/applications';
import GitPicker from './GitPicker.vue';
import { sortBy } from '@shell/utils/sort';
import { generateZip } from '@shell/utils/download';
import {
  APPLICATION_SOURCE_TYPE,
  EpinioApplicationChartResource,
  EpinioInfo,
  EpinioAppSource,
  EPINIO_APP_MANIFEST
} from '../../types';
import { EpinioAppInfo } from '../../types';
import { _EDIT } from '@shell/config/query-params';
import { AppUtils } from '../../utils/application';

interface FileWithRelativePath extends File {
  // For some reason TS throws this as missing at transpile time .. so recreate it
   readonly webkitRelativePath: string;
}

// Todo: Ensure this uses the same default as the backend.
const DEFAULT_BUILD_PACK = 'paketobuildpacks/builder-jammy-full:0.3.495';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppSource',
  props: {
    application: {},
    source: {},
    info: {},
    mode: {}
  },
  emits: ["change", "changeAppInfo", "changeAppConfig", "valid"],
  setup(__props: any, { emit: __emit }) {


const GIT_BASE_URL = {
  [APPLICATION_SOURCE_TYPE.GIT_HUB]: 'https://github.com',
  [APPLICATION_SOURCE_TYPE.GIT_LAB]: 'https://gitlab.com',
};

const store = useStore();

const t = store.getters['i18n/t'];

const props = __props;

const emit = __emit;

const isEdit = computed(() => props.mode === _EDIT);
const isView = computed(() => props.mode === 'view');

const manifestFileInput = ref<HTMLInputElement | null>(null);
const archiveFileInput = ref<HTMLInputElement | null>(null);
const folderFileInput = ref<HTMLInputElement | null>(null);
const fileDialogActive = ref(false);

// Defaults
const defaultBuilderImage = ref(props.info?.default_builder_image || DEFAULT_BUILD_PACK); 
const builderImageValue = ref(props.source?.builderImage?.value || defaultBuilderImage.value);

// Reactive State
const gitSkipTypeReset = ref(false);
const archive = reactive({
  tarball: props.source?.archive?.tarball || '',
  fileName: props.source?.type === 'folder' ? props.application?.origin?.path : props.source?.archive?.fileName || '',
});

const container = reactive({
  url: props.source?.container?.url || ''
});

const gitUrl = reactive({
  url: props.source?.gitUrl?.url || '',
  branch: props.source?.gitUrl?.branch || '',
  validGitUrl: false,
});

const git = reactive({
  usernameOrOrg: props.source?.git?.usernameOrOrg || '',
  repo: props.source?.git?.repo || '',
  commit: props.source?.git?.commit || '',
  branch: props.source?.git?.branch || '',
  url: props.source?.git?.url || '',
  sourceData: props.source?.git?.sourceData || {
    repos: [],
    branches: [],
    commits: []
  }
});

const builderImage = reactive({
  value: builderImageValue.value,
  default: builderImageValue.value === defaultBuilderImage.value
});

const appChart = ref(props.application.configuration?.appchart || props.source?.appChart || '');
const type = ref(props.source?.type || APPLICATION_SOURCE_TYPE.FOLDER);

// Derived and Computed
const types = Object.values(APPLICATION_SOURCE_TYPE).map(value => ({
  label: t(`epinio.applications.steps.source.${ value }.label`),
  value
}));

const namespaces = computed(() => sortBy(store.getters['epinio/all']('namespace'), 'name', false));
const appCharts = computed(() =>
  sortBy(store.getters['epinio/all']('appCharts'), 'name', false).map((ap: EpinioApplicationChartResource) => ({
    value: ap.meta.name,
    label: `${ap.meta.name} (${ap.short_description})`
  }))
);

const showBuilderImage = computed(() =>
  [APPLICATION_SOURCE_TYPE.ARCHIVE, APPLICATION_SOURCE_TYPE.FOLDER, APPLICATION_SOURCE_TYPE.GIT_URL, APPLICATION_SOURCE_TYPE.GIT_HUB, APPLICATION_SOURCE_TYPE.GIT_LAB].includes(type.value)
);

const gitSource = computed(() => ({
  type: type.value,
  selectedAccOrOrg: git.usernameOrOrg,
  selectedRepo: git.repo,
  selectedBranch: git.branch,
  selectedCommit: { sha: git.commit }
}));

const valid = ref(validate());

watch(type, () => {
  if (gitSkipTypeReset.value) {
    gitSkipTypeReset.value = false;
  } else {
    Object.assign(git, {});
  }
  update();
});

watch(valid, (val) => {
  emit('valid', val);
});

function validate() {
  switch (type.value) {
    case APPLICATION_SOURCE_TYPE.ARCHIVE:
    case APPLICATION_SOURCE_TYPE.FOLDER:
      return !!archive.tarball && !!builderImage.value;
    case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
      return !!container.url;
    case APPLICATION_SOURCE_TYPE.GIT_URL:
      return !!gitUrl.url && !!gitUrl.branch && !!builderImage.value && !!gitUrl.validGitUrl;
    case APPLICATION_SOURCE_TYPE.GIT_HUB:
    case APPLICATION_SOURCE_TYPE.GIT_LAB:
      return !!git.usernameOrOrg && !!git.url && !!git.repo && !!git.branch && !!git.commit;
  }
}

function update() {
  emit('change', {
    type: type.value,
    archive,
    container,
    gitUrl,
    builderImage: {
      value: builderImageValue.value,
      default:  builderImageValue.value === defaultBuilderImage.value
    },
    appChart: appChart.value,
    git
  });
  valid.value = validate();
}

function updateAppInfo(info: EpinioAppInfo) {
  emit('changeAppInfo', info);
}

function updateConfigurations(configs: string[]) {
  emit('changeAppConfig', configs);
}

function onImageType(defaultImage: boolean) {
  if (defaultImage) {
    builderImage.value = defaultBuilderImage.value;
  } else {
    builderImage.value = builderImageValue.value;
  }
  builderImage.default = defaultImage;
  update();
} 

function gitUpdate({ repo, selectedAccOrOrg, branch, commit, sourceData }: any) {
  if (!!selectedAccOrOrg && !!repo && !!commit && !!branch) {
    git.usernameOrOrg = selectedAccOrOrg;
    git.url = `${GIT_BASE_URL[type.value]}/${selectedAccOrOrg}/${repo.name}`;
    git.commit = commit;
    git.branch = branch;
    git.repo = repo;
    git.sourceData = sourceData;
    update();
    emit('valid', true);
  } else {
    update();
    emit('valid', false);
  }
}

function urlRule() {
  if (!gitUrl.url) return;

  const gitRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/gm;
  const result = gitRegex.exec(gitUrl.url);

  if (result && gitUrl.url === result[0]) {
    gitUrl.validGitUrl = true;
  } else {
    gitUrl.validGitUrl = false;
  }
}

function onFileSelected(file: File) {
  archive.tarball = file;
  archive.fileName = file.name;
  update();
}

function handleArchiveFileClick() {
  if (archiveFileInput.value) {
    fileDialogActive.value = true;
    archiveFileInput.value.click();
  }
}

function handleArchiveFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    onFileSelected(input.files[0]);
    input.value = ''; // Clear the input so the same file can be selected again if needed
  }
}

function handleFromManifestClick() {
  if (manifestFileInput.value) {
    fileDialogActive.value = true;
    manifestFileInput.value.click();
  }
}

function handleManifestFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    onManifestFileSelected(input.files[0] as any);
    input.value = ''; // Clear the input so the same file can be selected again if needed
  }
}

function onManifestFileSelected(file: string) {
  try {
    const parsed: any = jsyaml.load(file);
    const manifestType = AppUtils.getManifestSourceType(parsed.origin);
    gitSkipTypeReset.value = true;
    type.value = manifestType;

    switch (manifestType) {
      case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
        container.url = parsed.origin.container;
        break;
      case APPLICATION_SOURCE_TYPE.GIT_URL:
        gitUrl.url = parsed.origin.git.url;
        gitUrl.branch = parsed.origin.git.revision;
        break;
      case APPLICATION_SOURCE_TYPE.GIT_HUB:
      case APPLICATION_SOURCE_TYPE.GIT_LAB:
        Object.assign(git, AppUtils.getGitData(parsed.origin.git));
        break;
    }

    if (parsed.configuration) {
      appChart.value = parsed.configuration.appchart;
    }

    const appInfo: EpinioAppInfo = {
      meta: {
        name: parsed.name || '',
        namespace: namespaces.value?.[0]?.name || ''
      },
      configuration: {
        configurations: parsed.configuration?.configurations || [],
        instances: parsed.configuration.instances ?? 1,
        environment: parsed.configuration.environment || {},
        settings: parsed.configuration?.settings || {},
        routes: parsed.configuration.routes || []
      }
    };

    store.$router.replace({ query: { from: EPINIO_APP_MANIFEST } });
    update();
    updateAppInfo(appInfo);
    updateConfigurations(parsed.configuration.configurations || []);
  } catch (e) {
    console.error('Failed to parse manifest:', e);
  }
}

function handleFolderFileClick() {
  if (folderFileInput.value) {
    fileDialogActive.value = true;
    folderFileInput.value.click();
  }
}

function handleFolderFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    onFolderSelected(Array.from(input.files) as FileWithRelativePath[]);
    input.value = ''; // Clear the input so the same folder can be selected again if needed
  }
}

function onFolderSelected(files: FileWithRelativePath | FileWithRelativePath[]) {
  const safeFiles = Array.isArray(files) ? files : [files];
  let folderName = '';

  for (const f of safeFiles) {
    const paths = f.webkitRelativePath.split('/');
    if (paths.length > 1) {
      if (!folderName) {
        folderName = paths[0];
      } else if (folderName !== paths[0]) {
        folderName = '';
        break;
      }
    }
  }

  const filesToZip = safeFiles.reduce((res, f) => {
    let path = f.webkitRelativePath;
    if (folderName) {
      const parts = path.split('/');
      parts.shift();
      path = parts.join('/');
    }
    res[path] = f;
    return res;
  }, {} as { [key: string]: any });

  generateZip(filesToZip).then((zip: any) => {
    archive.tarball = zip;
    archive.fileName = folderName || 'folder';
    update();
  });
}

onMounted(() => {
  if (!appChart.value) {
    appChart.value = props.application.configuration?.appchart || props.source?.appChart || appCharts.value[0]?.value || appCharts.value[0];
  }
  update();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-dropdown", {
        style: {"flex":"1"},
        options: _unref(types),
        value: type.value,
        label: "Source Type",
        required: true,
        placeholder: "Select a source type",
        "data-testid": "epinio_app-source_type",
        onDropdownChange: _cache[0] || (_cache[0] = (e) => type.value = e.detail.value)
      }, null, 40, _hoisted_3),
      _cache[11] || (_cache[11] = _createTextVNode()),
      _createElementVNode("trailhand-button", {
        variant: "alternate",
        "data-testid": "epinio_app-source_manifest",
        onButtonClick: handleFromManifestClick
      }, "\n        From Manifest\n      ", 32),
      _cache[12] || (_cache[12] = _createTextVNode()),
      _createElementVNode("input", {
        ref_key: "manifestFileInput",
        ref: manifestFileInput,
        type: "file",
        class: "hidden-file-input",
        onChange: handleManifestFileChange,
        onCancel: _cache[1] || (_cache[1] = ($event: any) => (fileDialogActive.value = false))
      }, null, 544)
    ]),
    _cache[30] || (_cache[30] = _createTextVNode()),
    (type.value === _unref(APPLICATION_SOURCE_TYPE).ARCHIVE)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.archive.file.label')), 1),
          _cache[15] || (_cache[15] = _createTextVNode()),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("trailhand-text-input", {
              style: {"flex":"1"},
              value: archive.fileName,
              "data-testid": "epinio_app-source_archive_name",
              disabled: true,
              label: _unref(t)('epinio.applications.steps.source.archive.file.inputLabel'),
              required: true
            }, null, 8, _hoisted_6),
            _cache[13] || (_cache[13] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "alternate",
              "data-testid": "epinio_app-source_archive_file",
              onButtonClick: handleArchiveFileClick
            }, "\n            Select File\n          ", 32),
            _cache[14] || (_cache[14] = _createTextVNode()),
            _createElementVNode("input", {
              ref_key: "archiveFileInput",
              ref: archiveFileInput,
              type: "file",
              class: "hidden-file-input",
              accept: ".zip, .tar, .gz, .bz2, .xz",
              onChange: handleArchiveFileChange,
              onCancel: _cache[2] || (_cache[2] = ($event: any) => (fileDialogActive.value = false))
            }, null, 544)
          ])
        ]))
      : (type.value === _unref(APPLICATION_SOURCE_TYPE).FOLDER)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.folder.file.label')), 1),
            _cache[18] || (_cache[18] = _createTextVNode()),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("trailhand-text-input", {
                style: {"flex":"1"},
                value: archive.fileName,
                "data-testid": "epinio_app-source_folder_name",
                disabled: true,
                label: _unref(t)('epinio.applications.steps.source.folder.file.inputLabel'),
                required: true
              }, null, 8, _hoisted_9),
              _cache[16] || (_cache[16] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "alternate",
                "data-testid": "epinio_app-source_folder_file",
                onButtonClick: handleFolderFileClick
              }, "\n            Select Folder\n          ", 32),
              _cache[17] || (_cache[17] = _createTextVNode()),
              _createElementVNode("input", {
                ref_key: "folderFileInput",
                ref: folderFileInput,
                type: "file",
                webkitdirectory: "",
                class: "hidden-file-input",
                onChange: handleFolderFileChange,
                onCancel: _cache[3] || (_cache[3] = ($event: any) => (fileDialogActive.value = false))
              }, null, 544)
            ])
          ]))
        : (type.value === _unref(APPLICATION_SOURCE_TYPE).CONTAINER_URL)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.container_url.url.label')), 1),
              _cache[19] || (_cache[19] = _createTextVNode()),
              _createElementVNode("trailhand-text-input", {
                style: {"width":"100%"},
                value: container.url,
                "data-testid": "epinio_app-source_container",
                label: _unref(t)('epinio.applications.steps.source.container_url.url.inputLabel'),
                required: true,
                onTextInputChange: _cache[4] || (_cache[4] = (e) => { container.url = e.detail.value; update(); })
              }, null, 40, _hoisted_11)
            ]))
          : (type.value === _unref(APPLICATION_SOURCE_TYPE).GIT_URL)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.url.label')), 1),
                  _cache[20] || (_cache[20] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: gitUrl.url,
                    "data-testid": "epinio_app-source_git-url",
                    label: _unref(t)('epinio.applications.steps.source.git_url.url.inputLabel'),
                    placeholder: 'https://github.com/{user or org}/{repository}',
                    required: true,
                    onTextInputChange: _cache[5] || (_cache[5] = (e) => { gitUrl.url = e.detail.value; urlRule(); update(); })
                  }, null, 40, _hoisted_13),
                  _cache[21] || (_cache[21] = _createTextVNode()),
                  (gitUrl.url && !gitUrl.validGitUrl)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.error.label')), 1))
                    : _createCommentVNode("", true)
                ]),
                _cache[23] || (_cache[23] = _createTextVNode()),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.git_url.branch.label')), 1),
                  _cache[22] || (_cache[22] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: gitUrl.branch,
                    "data-testid": "epinio_app-source_git-branch",
                    label: _unref(t)('epinio.applications.steps.source.git_url.branch.inputLabel'),
                    required: true,
                    disabled: !gitUrl.validGitUrl,
                    onTextInputChange: _cache[6] || (_cache[6] = (e) => { gitUrl.branch = e.detail.value; update(); })
                  }, null, 40, _hoisted_16)
                ])
              ], 64))
            : (_openBlock(), _createBlock(GitPicker, {
                key: 4,
                value: gitSource.value,
                "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((gitSource).value = $event)),
                type: type.value,
                onChange: gitUpdate
              }, null, 8, ["value", "type"])),
    _cache[31] || (_cache[31] = _createTextVNode()),
    _createElementVNode("div", _hoisted_17, [
      _cache[27] || (_cache[27] = _createElementVNode("h3", null, "Advanced Settings", -1)),
      _cache[28] || (_cache[28] = _createTextVNode()),
      _createElementVNode("trailhand-dropdown", {
        style: {"width":"100%"},
        value: appChart.value,
        "data-testid": "epinio_app-source_appchart",
        label: _unref(t)('epinio.applications.steps.source.archive.appchart.label'),
        options: appCharts.value,
        disabled: isEdit.value || isView.value,
        placeholder: "Select an application chart",
        onDropdownChange: _cache[8] || (_cache[8] = (e) => { appChart.value = e.detail.value; update(); })
      }, null, 40, _hoisted_18),
      _cache[29] || (_cache[29] = _createTextVNode()),
      (showBuilderImage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
            _cache[24] || (_cache[24] = _createElementVNode("h4", null, "Paketo Builder Image", -1)),
            _cache[25] || (_cache[25] = _createTextVNode()),
            _createElementVNode("trailhand-checkbox", {
              checked: !builderImage.default,
              "data-testid": "epinio_app-source_builder-default",
              onCheckboxChange: _cache[9] || (_cache[9] = ($event: any) => (onImageType(!builderImage.default)))
            }, "\n            Use Custom Builder Image\n          ", 40, _hoisted_20),
            _cache[26] || (_cache[26] = _createTextVNode()),
            _createElementVNode("trailhand-text-input", {
              style: {"width":"100%"},
              value: builderImageValue.value,
              "data-testid": "epinio_app-source_builder-value",
              disabled: builderImage.default,
              onTextInputChange: _cache[10] || (_cache[10] = (e) => { builderImageValue.value = e.detail.value; update(); })
            }, null, 40, _hoisted_21)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})